/* eslint-disable camelcase */
/* eslint-disable @typescript-eslint/no-explicit-any */

import { useQueryClient } from '@tanstack/react-query';

import { usePatchPlotGroup, usePostPlotGroup } from '@/api/rest/resources/organization';
import { FilterDefinition } from '@/api/rest/resources/types/organization';
import {
  PLOT_GROUP_FILTER_OPERATOR,
  PlotGroup,
  PlotGroupFiltersEnum,
  PlotGroupGroupByEnum,
  PlotGroupSortEnum,
  PlotLabelValue,
  SortOptions,
} from '@/api/rest/resources/types/plotGroup';
import {
  usePlotGroupId,
  usePlotGroupNames,
  useSetPlotGroupId,
  useSetPlotGroupName,
} from '@/components/PlotGroups/PlotGroupsContextProvider';
import { useOrganizationId } from '@/pages/shared/hooks/useOrganizationId';
import { assertIsDefined } from '@/utils/assert';
import { isNullish } from '@/utils/booleans';

export type CreateEditGroupSubmitProps = {
  organizationId: string;
  plotGroup: PlotGroup;
  additionalPlots?: string[];
  onSuccess?: () => void;
  workstreamId?: string;
};
export const useCreateGroup = () => {
  const postPlotGroupMutation = usePostPlotGroup();

  const submit = async ({
    organizationId,
    plotGroup,
    additionalPlots,
    onSuccess,
    workstreamId,
  }: CreateEditGroupSubmitProps) => {
    let group = plotGroup;
    if (additionalPlots && additionalPlots.length > 0) {
      group = { ...plotGroup, additional_plot_ids: additionalPlots };
    }
    assertIsDefined(group.name, 'plotGroup.name');
    assertIsDefined(organizationId, 'organizationId');

    const formattedPlotGroup = formatPlotGroup(group, group.name);

    const newGroup = await postPlotGroupMutation.mutateAsync({
      pathVariables: { id: organizationId },
      bodyData: formattedPlotGroup,
      queryParams: workstreamId ? { workstream_id: workstreamId } : undefined,
    });

    onSuccess?.();

    return newGroup;
  };

  const isSubmitting = postPlotGroupMutation.isPending;

  return { submit, isSubmitting };
};

export const useEditGroup = () => {
  const patchPlotGroupMutation = usePatchPlotGroup();

  const submit = async ({ organizationId, plotGroup, additionalPlots, onSuccess }: CreateEditGroupSubmitProps) => {
    let group = plotGroup;
    // TODO: MVP-7279 remove and test against parent child with wider refactor
    if (additionalPlots && additionalPlots.length > 0) {
      group = { ...group, additional_plot_ids: additionalPlots };
    }
    assertIsDefined(group.name, 'plotGroup.name');
    assertIsDefined(organizationId, 'organizationId');

    const formattedPlotGroup = formatPlotGroup(group, group.name);

    const newGroup = await patchPlotGroupMutation.mutateAsync({
      pathVariables: { org_id: organizationId, id: plotGroup.id ?? '' },
      bodyData: formattedPlotGroup,
    });

    onSuccess?.();

    return newGroup;
  };

  const isSubmitting = patchPlotGroupMutation.isPending;

  return { submit, isSubmitting };
};

export const useCreatePlotGroup = () => {
  const postPlotGroupMutation = usePostPlotGroup();
  const { invalidatePlotGroupRelatedQueries } = usePlotGroupInvalidateQueries();

  const { DEFAULT, NO_GROUPING } = usePlotGroupNames();

  const orgId = useOrganizationId();
  const { setActivePlotGroupId } = useSetPlotGroupId();
  const { setActivePlotGroupName } = useSetPlotGroupName();

  const submit = async (
    plotGroup: PlotGroup,
    organizationIdArg?: string,
    // eslint-disable-next-line default-param-last
    activatePlotGroup = true,
    allowGrouping?: boolean,
  ) => {
    const shouldAllowGrouping = allowGrouping !== undefined ? allowGrouping : plotGroup.name !== NO_GROUPING;

    const formattedPlotGroup = formatPlotGroup(plotGroup, DEFAULT, shouldAllowGrouping);
    const organizationId = organizationIdArg ?? orgId;

    assertIsDefined(organizationId, 'organizationId');

    const { id, name } = await postPlotGroupMutation.mutateAsync({
      pathVariables: { id: organizationId },
      bodyData: formattedPlotGroup,
    });

    if (activatePlotGroup) {
      setActivePlotGroupId(id);
      setActivePlotGroupName(name);
    }

    invalidatePlotGroupRelatedQueries(organizationId);

    return { id };
  };

  const isSubmitting = postPlotGroupMutation.isPending;

  return { submit, isSubmitting };
};

export const useUpdatePlotGroup = () => {
  const { invalidatePlotGroupRelatedQueries } = usePlotGroupInvalidateQueries();
  const updatePlotGroupMutation = usePatchPlotGroup();

  const groupId = usePlotGroupId();
  const orgId = useOrganizationId();

  const { DEFAULT, NO_GROUPING, DETAILS } = usePlotGroupNames();

  const submit = async (
    plotGroup: PlotGroup,
    plotGroupIdArg?: string,
    organizationIdArg?: string,
    allowGrouping?: boolean,
  ) => {
    const organizationId = organizationIdArg ?? orgId;
    const plotGroupId = plotGroupIdArg ?? groupId;

    assertIsDefined(organizationId, 'organizationId');
    assertIsDefined(plotGroupId, 'plotGroupId');

    const allowedPlotGroupNames = [DEFAULT, NO_GROUPING, DETAILS];
    if (!allowedPlotGroupNames.includes(plotGroup.name ?? '')) {
      throw new Error(
        `Plot group name must be "${allowedPlotGroupNames.join(', ')}". Other plot groups cannot be edited, the name received is: ${plotGroup.name} (id: ${plotGroupId})`,
      );
    }

    const shouldAllowGrouping = allowGrouping !== undefined ? allowGrouping : plotGroup.name !== NO_GROUPING;
    const formattedPlotGroup = formatPlotGroup(plotGroup, DEFAULT, shouldAllowGrouping);

    const { id } = await updatePlotGroupMutation.mutateAsync({
      pathVariables: { org_id: organizationId, id: plotGroupId },
      bodyData: formattedPlotGroup,
    });

    invalidatePlotGroupRelatedQueries(organizationId);

    return { id };
  };

  const isSubmitting = updatePlotGroupMutation.isPending;

  return { submit, isSubmitting };
};

export const usePlotGroupInvalidateQueries = () => {
  const queryClient = useQueryClient();

  const invalidatePlotGroupRelatedQueries = (organizationId: string) => {
    queryClient.resetQueries({ queryKey: ['plots', 'organization'] }); // TODO: MVP-CW swap orgn and plots order if BE changed
    queryClient.resetQueries({ queryKey: ['organization'] });

    queryClient.resetQueries({
      queryKey: ['plot-label-names', { organization_id: organizationId }],
    });
    queryClient.resetQueries({
      queryKey: ['plot-label-names', 'values'],
    });
  };

  return { invalidatePlotGroupRelatedQueries };
};

// eslint-disable-next-line sonarjs/cognitive-complexity
const formatPlotGroup = (plotGroup: PlotGroup, defaultGroupName: string, allowGrouping = true) => {
  assertIsDefined(defaultGroupName, 'defaultGroupName');

  const field_filters = (Object.entries(plotGroup.filters) as [keyof typeof PLOT_GROUP_FILTER_OPERATOR, any][]).reduce<
    FilterDefinition['field_filters']
  >((acc, [field, value]) => {
    const isArray = Array.isArray(value);
    if (isArray && value?.length === 0) {
      return acc;
    }

    if (isNullish(value)) {
      return acc;
    }

    let operator = PLOT_GROUP_FILTER_OPERATOR[`${field}`];

    if (!operator) {
      throw new Error(`No operator defined for filter: ${field}`);
    }

    let sanitizedValue = value as any;

    if (field === PlotGroupFiltersEnum.plot__labels__id && Array.isArray(value)) {
      sanitizedValue = (value as PlotLabelValue[]).map((labelValue) => labelValue.id);
    }

    if (field === PlotGroupFiltersEnum.deforested) {
      sanitizedValue = 0; // The value is always 0, what matters is the operator (for deforestation, true, at least one of values of the timeseries in the deforestation fact must be > 0 (gt); for no deforestation, false, all the values in the deforestation timeseries must be === 0 (max))
      operator = (
        value === 'true' || value === true
          ? PLOT_GROUP_FILTER_OPERATOR[PlotGroupFiltersEnum.deforested].true
          : PLOT_GROUP_FILTER_OPERATOR[PlotGroupFiltersEnum.deforested].false
      ) as typeof operator;
    }

    if (typeof operator === 'object') {
      throw new Error(`Operator for field ${field} got the wrong type. Operator: ${JSON.stringify(operator)}.`);
    }

    if (operator === 'in' && !Array.isArray(sanitizedValue)) {
      sanitizedValue = [sanitizedValue];
    }

    acc.push({
      field,
      operator,
      value: sanitizedValue,
    });
    return acc;
  }, []);

  const sort_orders = Object.entries(plotGroup.sort).reduce<
    Array<{
      field: PlotGroupSortEnum;
      direction: SortOptions;
    }>
  >((acc, [field, direction]) => {
    if (isNullish(direction)) {
      return acc;
    }

    acc.push({
      field: field as PlotGroupSortEnum,
      direction,
    });
    return acc;
  }, []);

  const group_by = allowGrouping
    ? Object.entries(plotGroup.groupBy).reduce<
        Array<{
          label_name_id: string | null;
        }>
      >((acc, [field, value]) => {
        if (isNullish(value) || Array.isArray(value)) {
          return acc;
        }

        if (field === PlotGroupGroupByEnum.labels && value.id) {
          acc.push({
            label_name_id: value.id,
          });
        }

        return acc;
      }, [])
    : [];

  return {
    filter_definition: {
      field_filters,
      sort_orders,
      group_by,
    },
    name: plotGroup.name ?? defaultGroupName,
    additional_plot_ids: plotGroup.additional_plot_ids || [],
  };
};
